import React, { useState, useEffect } from 'react';
import { Search, Filter, UserPlus, Users, RefreshCw, Plus } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { CommunityService } from '../../services/communityService';
import LawyerProfileCard from './LawyerProfileCard';
import { SectionHeader } from '../ui';
import type { LawyerProfile, CommunityConnection } from '../../types/community';

interface NetworkSectionProps {
  searchQuery: string;
}

const NetworkSection: React.FC<NetworkSectionProps> = ({ searchQuery }) => {
  const { language, isRTL } = useLocalization();
  const [lawyers, setLawyers] = useState<LawyerProfile[]>([]);
  const [connections, setConnections] = useState<CommunityConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('all');
  const [selectedExperience, setSelectedExperience] = useState<string>('all');
  const [showOnlyConnected, setShowOnlyConnected] = useState(false);

  useEffect(() => {
    loadLawyers();
    loadConnections();
  }, []);

  const loadLawyers = async () => {
    try {
      setLoading(true);
      const lawyersData = await CommunityService.searchLawyers('');
      setLawyers(lawyersData);
    } catch (error) {
      console.error('Error loading lawyers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    try {
      // This would typically get the current user's connections
      // For now, we'll use mock data
      setConnections([]);
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const getUniqueSpecialties = () => {
    const specialties = new Set<string>();
    lawyers.forEach(lawyer => {
      lawyer.specialties.forEach(specialty => specialties.add(specialty));
    });
    return Array.from(specialties);
  };

  const getExperienceRanges = () => {
    return [
      { value: '0-2', label: language === 'ar' ? '0-2 سنوات' : '0-2 years' },
      { value: '3-5', label: language === 'ar' ? '3-5 سنوات' : '3-5 years' },
      { value: '6-10', label: language === 'ar' ? '6-10 سنوات' : '6-10 years' },
      { value: '10+', label: language === 'ar' ? '10+ سنوات' : '10+ years' }
    ];
  };

  const isConnected = (lawyerId: string) => {
    return connections.some(conn => 
      (conn.requesterId === lawyerId || conn.receiverId === lawyerId) && 
      conn.status === 'accepted'
    );
  };

  const getConnectionStatus = (lawyerId: string): 'pending' | 'accepted' | 'declined' | undefined => {
    const connection = connections.find(conn => 
      (conn.requesterId === lawyerId || conn.receiverId === lawyerId)
    );
    const status = connection?.status;
    return status === 'blocked' ? undefined : status;
  };

  const filteredLawyers = lawyers.filter(lawyer => {
    const matchesSearch = searchQuery === '' || 
      lawyer.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lawyer.bio.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lawyer.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSpecialty = selectedSpecialty === 'all' || 
      lawyer.specialties.includes(selectedSpecialty);
    
    const matchesExperience = selectedExperience === 'all' || 
      (selectedExperience === '0-2' && lawyer.experience >= 0 && lawyer.experience <= 2) ||
      (selectedExperience === '3-5' && lawyer.experience >= 3 && lawyer.experience <= 5) ||
      (selectedExperience === '6-10' && lawyer.experience >= 6 && lawyer.experience <= 10) ||
      (selectedExperience === '10+' && lawyer.experience > 10);
    
    const matchesConnectionFilter = !showOnlyConnected || isConnected(lawyer.userId);
    
    return matchesSearch && matchesSpecialty && matchesExperience && matchesConnectionFilter;
  });

  const handleViewProfile = (profile: LawyerProfile) => {
    // Navigate to lawyer profile
    console.log('View profile:', profile.userId);
  };

  const handleConnect = async (lawyerId: string) => {
    try {
      // Implement connection request
      console.log('Connect to lawyer:', lawyerId);
    } catch (error) {
      console.error('Error connecting to lawyer:', error);
    }
  };

  const handleRefresh = () => {
    loadLawyers();
    loadConnections();
  };

  const handleAddLawyer = () => {
    // Navigate to add lawyer page or open modal
    console.log('Add new lawyer');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 animate-pulse">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
                </div>
              </div>
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title={language === 'ar' ? 'شبكة المحامين' : 'Lawyer Network'}
        description={language === 'ar' ? 'اكتشف واتصل مع المحامين في مجتمعك' : 'Discover and connect with lawyers in your community'}
        language={language}
        isRTL={isRTL}
        refreshButton={{
          onClick: handleRefresh,
          disabled: loading
        }}
        addButton={{
          label: language === 'ar' ? 'إضافة محامي' : 'Add Lawyer',
          onClick: handleAddLawyer
        }}
      >
        <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${isRTL ? 'md:grid-cols-4' : ''}`}>
          {/* Specialty Filter */}
          <div className="relative">
            <select
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
              className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent w-full"
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            >
              <option value="all">
                {language === 'ar' ? 'جميع التخصصات' : 'All Specialties'}
              </option>
              {getUniqueSpecialties().map((specialty) => (
                <option key={specialty} value={specialty}>
                  {specialty}
                </option>
              ))}
            </select>
          </div>

          {/* Experience Filter */}
          <div className="relative">
            <select
              value={selectedExperience}
              onChange={(e) => setSelectedExperience(e.target.value)}
              className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent w-full"
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            >
              <option value="all">
                {language === 'ar' ? 'جميع الخبرات' : 'All Experience'}
              </option>
              {getExperienceRanges().map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          {/* Connection Filter */}
          <div className="flex items-center">
            <label className={`flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <input
                type="checkbox"
                checked={showOnlyConnected}
                onChange={(e) => setShowOnlyConnected(e.target.checked)}
                className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
              />
              {language === 'ar' ? 'المتصلين فقط' : 'Connected Only'}
            </label>
          </div>

          {/* Results Count */}
          <div className={`flex items-center ${isRTL ? 'justify-start' : 'justify-end'}`}>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {filteredLawyers.length} {language === 'ar' ? 'محامي' : 'lawyers'}
            </span>
          </div>
        </div>
      </SectionHeader>

      {/* Lawyers Grid */}
      {filteredLawyers.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-12 h-12 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
            {language === 'ar' ? 'لا يوجد محامين' : 'No Lawyers Found'}
          </h3>
          <p className="text-slate-600 dark:text-slate-300">
            {searchQuery ? 
              (language === 'ar' ? 'لم نجد محامين يطابقون بحثك' : 'No lawyers match your search') :
              (language === 'ar' ? 'لا يوجد محامين في الشبكة حالياً' : 'No lawyers in the network yet')
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLawyers.map((lawyer) => (
            <LawyerProfileCard
              key={lawyer.userId}
              profile={lawyer}
              onViewProfile={() => handleViewProfile(lawyer)}
              onConnect={() => handleConnect(lawyer.userId)}
              isConnected={isConnected(lawyer.userId)}
              connectionStatus={getConnectionStatus(lawyer.userId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NetworkSection;
